import { defineComponent as _defineComponent } from 'vue'
import JudgingPoint from '@/components/Battle/Prelims/JudgingPoint.vue'
import { IParticipant, PrelimsJudgment } from '@/types/battle'
import { computed, onMounted, ref, watchEffect } from 'vue'
import { mdiChevronDown, mdiChevronUp, mdiClose, mdiStarOutline } from '@mdi/js'
import { EventDB } from '@/enitites/event'
import { useImage } from '@/shared/composables/image'
import { BattleDB } from '@/enitites/battle'
import store from '@/store'
import api from '@/api'


export default /*#__PURE__*/_defineComponent({
  __name: 'PrelimsRoundScoreCrews',
  props: {
    participant: null,
    battle: null,
    canEdit: { type: Boolean },
    canSetGone: { type: Boolean },
    isHighlighted: { type: Boolean },
    isGrayedOut: { type: Boolean },
    isCrew: { type: Boolean },
    event: null,
    judgment: null
  },
  emits: ["cancel", "gone", "edit", "update:prelims"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'cancel'): void
  (e: 'gone', id: number): void
  (e: 'edit'): void
  (e: 'update:prelims', value: unknown): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  participant: IParticipant
  battle: BattleDB
  canEdit: boolean
  canSetGone: boolean
  isHighlighted: boolean
  isGrayedOut: boolean
  isCrew?: boolean
  event: EventDB
  judgment: PrelimsJudgment
};



const { getAvatar } = useImage()

const roundsForJudgment = ref(
  Array.from({ length: props.battle.crewSize }, (_, index) => ({
    ...props.judgment,
    round: index + 1,
  }))
)

const getUserDB = computed(() => store.getters.getUserDB)
const rounds = ref(
  roundsForJudgment.value.map((judgment) => ({
    round: judgment.round,
    name: 'Round',
    judgment: {
      ...judgment,
      points:
        props.participant.prelims.rounds[judgment.round]?.find((judge) => judge.id === getUserDB.value?.id)?.points ||
        0,
    },
  }))
)

const currentRound = ref(0)

const isEditMode = ref(false)
const isShowActions = ref(false)
const availableMainPoints = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const isPointDetailsOpened = ref(false)
const loading = ref(false)
const isShowDoneButton = ref(false)

const roundsMap = computed<Map<number, PrelimsJudgment[]>>(() => {
  const map = new Map<number, PrelimsJudgment[]>()
  if (props.participant?.prelims?.rounds) {
    Object.entries(props.participant.prelims.rounds).forEach(([roundKey, judges]) => {
      map.set(Number(roundKey), judges)
    })
  }
  return map
})

async function updatePoints() {
  loading.value = true
  try {
    let judgments = rounds.value.map((round) => round.judgment)

    const allJudgmentsAreZero = judgments.every((judgement) => judgement.points === 0)
    if (allJudgmentsAreZero) {
      judgments = judgments.filter((judgement) => {
        return roundsMap.value.get(judgement.round)?.length
      })
    } else {
      judgments = judgments.filter((judgement) => {
        return judgement.points || roundsMap.value.get(judgement.round)?.length
      })
    }

    const updatedPrelims = await api.prelims.setJudgment(props.battle.id, judgments)

    if (updatedPrelims) {
      emit('update:prelims', updatedPrelims)
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function toggleEditMode() {
  if (isEditMode.value) {
    isEditMode.value = false
    isShowActions.value = false
    isShowDoneButton.value = false
    emit('cancel')
    updatePoints()
  } else {
    isEditMode.value = true
    isShowActions.value = true
    isShowDoneButton.value = true
    emit('edit')
  }
}

const roundsData = computed(() => {
  if (!props.participant?.prelims?.rounds) {
    return []
  }

  const judgesMap = new Map()

  Object.entries(props.participant.prelims.rounds).forEach(([round, judges]) => {
    judges.forEach((judge) => {
      if (!judge || !judge.id) return

      if (!judgesMap.has(judge.id)) {
        judgesMap.set(judge.id, { ...judge, pointsPerRound: {} })
      }
      judgesMap.get(judge.id).pointsPerRound[round] = judge.points
    })
  })

  return Array.from(judgesMap.values())
})
const changeTabTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const isAnyRoundWithoutPoints = computed(() => roundsForJudgment.value.some((round) => !round.points))

function startTimeOutForChangingTab() {
  if (changeTabTimeout.value) {
    clearTimeout(changeTabTimeout.value)
  }

  changeTabTimeout.value = setTimeout(() => {
    if (rounds.value.length - 1 > currentRound.value) {
      currentRound.value += 1
    }
  }, 10000)
}

function selectMainPoint(point: number, index: number) {
  const prevPoint = rounds.value[index].judgment.points || 0
  const diff = point - Math.floor(prevPoint)
  rounds.value[index].judgment.points += diff || -point
  if (!isAnyRoundWithoutPoints.value) return
  updatePoints()
  startTimeOutForChangingTab()
}

function selectHalfPoint() {
  if (Number.isSafeInteger(rounds.value[currentRound.value].judgment.points)) {
    rounds.value[currentRound.value].judgment.points += 0.5
  } else {
    rounds.value[currentRound.value].judgment.points -= 0.5
  }
  if (!isAnyRoundWithoutPoints.value) return
  updatePoints()
  startTimeOutForChangingTab()
}

const judgeRefs = ref<HTMLElement[]>([])
const isJudgeElScrollable = ref(false)
const isShowFullListOfPoints = ref<boolean[]>(roundsData.value.map(() => false))

function toggleFullList(index: number) {
  isShowFullListOfPoints.value[index] = !isShowFullListOfPoints.value[index]
  isShowFullListOfPoints.value = [...isShowFullListOfPoints.value]
}

watchEffect(() => {
  isJudgeElScrollable.value = judgeRefs.value.some((el) => el.scrollWidth > el.clientWidth)
})
onMounted(() => {
  if (props.judgment && props.canEdit && !props.judgment.points) {
    isEditMode.value = true
    emit('edit')
  }
})

return { __sfc: true,props, getAvatar, emit, roundsForJudgment, getUserDB, rounds, currentRound, isEditMode, isShowActions, availableMainPoints, isPointDetailsOpened, loading, isShowDoneButton, roundsMap, updatePoints, toggleEditMode, roundsData, changeTabTimeout, isAnyRoundWithoutPoints, startTimeOutForChangingTab, selectMainPoint, selectHalfPoint, judgeRefs, isJudgeElScrollable, isShowFullListOfPoints, toggleFullList, JudgingPoint, mdiChevronDown, mdiChevronUp, mdiClose, mdiStarOutline }
}

})